import { useState } from 'react';
import { MdGroups, MdAdd, MdDelete, MdEdit, MdVisibility } from 'react-icons/md';
import Modal, { DialogBody, DialogFooter } from '../ui/Dialog';
import { Badge, Button, ConfirmDialog, EmptyState, Field, IconButton } from '../ui';
import PersonPicker from './PersonPicker';
import { createGroup, deleteGroup, resolveGroup, updateGroup } from '../../api/addressBook';
import { messaggioErrore } from '../../utils/apiError';

/** Gruppi rubrica: insiemi di PERSONE, non di identità.
 *  L'account con cui inviare si sceglie dopo, e chi non è raggiungibile da
 *  quell'account viene mostrato prima di inviare, non scoperto dopo. */
export default function GroupsPanel({ groups, people, connection, connections, onChanged }) {
  const [bozza, setBozza] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);
  const [verifica, setVerifica] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');

  const esegui = async (azione) => {
    setBusy(true); setErrore('');
    try {
      await azione();
      onChanged?.();
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const salva = () => esegui(async () => {
    const corpo = { name: bozza.name, members: bozza.members };
    if (bozza.id) await updateGroup(bozza.id, corpo); else await createGroup(corpo);
    setBozza(null);
  });

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-base font-semibold text-ink">
          <MdGroups size={18} aria-hidden /> Gruppi rubrica
        </h2>
        <Button size="sm" icon={MdAdd} onClick={() => setBozza({ name: '', members: [] })}>
          Nuovo gruppo
        </Button>
      </div>
      <p className="text-xs text-muted">
        Un invio al gruppo manda un messaggio privato a ogni persona. Non è un gruppo WhatsApp.
      </p>

      {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

      {groups.length === 0 ? (
        <EmptyState icon={MdGroups} title="Nessun gruppo"
          hint="Raggruppa le persone a cui scrivi spesso insieme." />
      ) : (
        <ul className="space-y-2">
          {groups.map(gruppo => (
            <li key={gruppo.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3">
              <strong className="text-sm text-ink">{gruppo.name}</strong>
              <Badge>{gruppo.members.length} persone</Badge>
              {gruppo.unreachable_hint > 0 && (
                <Badge tone="warning">{gruppo.unreachable_hint} senza identità</Badge>
              )}
              <span className="flex-1" />
              <Button size="sm" icon={MdVisibility} disabled={!connection}
                onClick={() => esegui(async () => setVerifica({
                  gruppo, esito: await resolveGroup(gruppo.id, Number(connection)),
                }))}>
                Chi riceverà
              </Button>
              <IconButton icon={MdEdit} label={`Modifica ${gruppo.name}`}
                onClick={() => setBozza({ ...gruppo, members: [...gruppo.members] })} />
              <IconButton icon={MdDelete} label={`Elimina ${gruppo.name}`} tone="danger"
                onClick={() => setDaEliminare(gruppo)} />
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!bozza} onClose={() => setBozza(null)} size="lg"
        label={bozza?.id ? 'Modifica gruppo' : 'Nuovo gruppo rubrica'}>
        <DialogBody className="space-y-3">
          <Field label="Nome del gruppo" required data-autofocus value={bozza?.name || ''}
            onChange={e => setBozza({ ...bozza, name: e.target.value })} />
          <PersonPicker people={people} selected={bozza?.members || []}
            onChange={members => setBozza({ ...bozza, members })}
            hint="Ogni persona compare una volta sola, anche se ha più identità WhatsApp." />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setBozza(null)}>Annulla</Button>
          <Button variant="primary" loading={busy} disabled={!bozza?.name?.trim()} onClick={salva}>
            Salva gruppo
          </Button>
        </DialogFooter>
      </Modal>

      <Modal open={!!verifica} onClose={() => setVerifica(null)} size="md"
        label={`Chi riceverà: ${verifica?.gruppo.name || ''}`}
        description={`Inviando da ${connections.find(c => String(c.id) === String(connection))?.phone_number || 'questo account'}`}>
        <DialogBody className="space-y-4">
          <div>
            <h3 className="mb-1 text-sm font-semibold text-success">
              Riceveranno ({verifica?.esito.reachable.length || 0})
            </h3>
            <ul className="space-y-1 text-sm text-ink">
              {verifica?.esito.reachable.map(r => (
                <li key={r.person} className="flex justify-between gap-2">
                  <span>{r.name}</span>
                  <span className="tabular-nums text-muted">{r.phone || r.whatsapp_id}</span>
                </li>
              ))}
              {!verifica?.esito.reachable.length && <li className="text-muted">Nessuno.</li>}
            </ul>
          </div>
          {verifica?.esito.missing.length > 0 && (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-warning">
                Non riceveranno ({verifica.esito.missing.length})
              </h3>
              <ul className="space-y-1 text-sm text-ink">
                {verifica.esito.missing.map(m => (
                  <li key={m.person}>{m.name} — <span className="text-muted">{m.reason}</span></li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted">
                Per raggiungerle serve una loro identità su questo account: apri la scheda della
                persona e collegala.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={() => setVerifica(null)}>Chiudi</Button>
        </DialogFooter>
      </Modal>

      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)} danger loading={busy}
        title="Eliminare il gruppo?"
        message={`"${daEliminare?.name}" verrà eliminato. Le persone restano in rubrica; le automazioni che usano questo gruppo smetteranno di funzionare.`}
        confirmLabel="Elimina"
        onConfirm={() => esegui(async () => { await deleteGroup(daEliminare.id); setDaEliminare(null); })} />
    </section>
  );
}
